/**
 * TenantDetailsPanel Component
 *
 * The "Tenant details" tab body for the selected org. Shows the org's descriptive
 * fields, its creation metadata (customer id, subscription, entitlements,
 * limits), and its user count — fetched from `GET /api/tenant-management/orgs/{id}`.
 *
 * When the caller holds tenant-management authority AND the org is a deletable
 * customer org (not the vendor org, not the default org), it exposes a guarded
 * "Delete tenant" action.
 *
 * @module components/TenantDetailsPanel
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import type { TenantDetails } from '../types';
import { useTenantsStore } from '../stores/useTenantsStore';
import { formatErrorMessage } from '../utils';
import { ConfirmDeleteTenantDialog } from './ConfirmDeleteTenantDialog';
import { EditTenantDialog } from './EditTenantDialog';

// =============================================================================
// Types
// =============================================================================

export interface TenantDetailsPanelProps {
  /** The org to show details for. */
  orgId: string;
  /** Whether the caller may delete tenants (gates the Delete affordance). */
  canManageTenants: boolean;
  /** Called after a successful delete, with a confirmation message. */
  onDeleted?: (message: string) => void;
  /** Called after a successful edit, with a confirmation message. */
  onUpdated?: (message: string) => void;
}

// =============================================================================
// Helpers
// =============================================================================

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Box
      data-testid={`tenant-detail-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          display: 'block',
        }}
      >
        {label}
      </Typography>
      <Typography variant="body1" component="div">
        {children}
      </Typography>
    </Box>
  );
}

function limitLabel(n: number): string {
  return n > 0 ? String(n) : 'No limit';
}

// =============================================================================
// Component
// =============================================================================

export function TenantDetailsPanel({
  orgId,
  canManageTenants,
  onDeleted,
  onUpdated,
}: TenantDetailsPanelProps) {
  const loadedDetails = useTenantsStore((state) => state.details);
  const isLoading = useTenantsStore((state) => state.isLoading);
  const error = useTenantsStore((state) => state.error);
  const loadTenant = useTenantsStore((state) => state.loadTenant);
  const deleteTenant = useTenantsStore((state) => state.deleteTenant);

  const [confirmDelete, setConfirmDelete] = useState<TenantDetails | null>(
    null,
  );
  const [editTarget, setEditTarget] = useState<TenantDetails | null>(null);

  // Fetch on mount / when the selected org changes.
  useEffect(() => {
    void loadTenant(orgId);
  }, [orgId, loadTenant]);

  // The store is a singleton, so its `details` may briefly hold the previously-
  // viewed org while a new load is in flight. Only treat it as current when its
  // id matches this panel's org.
  const details =
    loadedDetails && loadedDetails.id === orgId ? loadedDetails : null;

  const handleConfirmDelete = useCallback(
    async (targetOrgId: string) => {
      await deleteTenant(targetOrgId);
      // Success — let the parent refresh the picker, clear selection, and notify.
      // Name comes from the confirmed-tenant snapshot (still non-null here, before
      // the dialog closes), not the singleton store's `details`, which a concurrent
      // load could have swapped.
      onDeleted?.(`Tenant "${confirmDelete?.name ?? ''}" deleted.`);
    },
    [deleteTenant, confirmDelete, onDeleted],
  );

  // The panel body varies by load state, but the dialogs below must stay
  // mounted through every branch: a successful edit refetches the details
  // (isLoading flips true mid-save), and unmounting the open dialog then
  // remounts it with fresh state — it flashes back to editable before closing.
  const body = renderBody();

  function renderBody(): React.ReactNode {
    if (isLoading || (!details && !error)) {
      return (
        <Box data-testid="tenant-details-loading">
          <Skeleton variant="text" width="30%" height={32} />
          <Skeleton
            variant="rectangular"
            height={180}
            sx={{ mt: 2, borderRadius: 1 }}
          />
        </Box>
      );
    }

    if (error && !details) {
      return (
        <Alert
          severity="error"
          data-testid="tenant-details-error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => void loadTenant(orgId)}
              data-testid="tenant-details-retry"
            >
              Retry
            </Button>
          }
        >
          {formatErrorMessage(error)}
        </Alert>
      );
    }

    if (!details) {
      return null;
    }

    const deletable = canManageTenants && !details.vendor && !details.isDefault;
    // Editing follows the same protection rule as deletion: the vendor org and
    // the instance default org are read-only (the backend refuses them too).
    const editable = deletable;
    // The backend can return `null` for an empty entitlements list (Go nil slice);
    // normalise so downstream `.length`/`.map` never hit null.
    const entitlements = details.entitlements ?? [];

    return (
      <Box data-testid="tenant-details-panel">
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            sx={{
              alignItems: 'center',
            }}
          >
            <Typography
              variant="h6"
              component="h2"
              data-testid="tenant-details-name"
            >
              {details.name}
            </Typography>
            {details.vendor && (
              <Chip
                size="small"
                color="primary"
                label="Vendor"
                data-testid="tenant-details-vendor-chip"
              />
            )}
          </Stack>

          <Stack direction="row" spacing={1}>
            {editable && (
              <Button
                variant="outlined"
                startIcon={<EditOutlinedIcon />}
                onClick={() => setEditTarget(details)}
                data-testid="tenant-details-edit-button"
              >
                Edit tenant
              </Button>
            )}
            {deletable && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteForeverIcon />}
                onClick={() => setConfirmDelete(details)}
                data-testid="tenant-details-delete-button"
              >
                Delete tenant
              </Button>
            )}
          </Stack>
        </Stack>

        {details.vendor && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            data-testid="tenant-details-vendor-notice"
          >
            This is the ModernPath vendor organization — it is read-only and
            cannot be deleted.
          </Alert>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2.5,
          }}
        >
          <Field label="Primary domain">{details.primaryDomain || '—'}</Field>
          <Field label="Customer ID">{details.customerId || '—'}</Field>
          <Field label="Subscription status">
            {details.subscriptionStatus || '—'}
          </Field>
          <Field label="Users">{details.userCount}</Field>
          <Field label="Seat limit">{limitLabel(details.seatLimit)}</Field>
          <Field label="Project limit">{limitLabel(details.projectLimit)}</Field>
        </Box>

        <Divider sx={{ my: 2.5 }} />

        <Field label="Entitlements">
          {/* The backend serialises an org with no entitlements as `null` (Go's nil
              slice), not `[]`, so normalise before reading .length / mapping. */}
          {entitlements.length > 0 ? (
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              sx={{
                flexWrap: 'wrap',
                mt: 0.5,
              }}
            >
              {entitlements.map((e) => (
                <Chip
                  key={e}
                  size="small"
                  label={e}
                  data-testid={`tenant-entitlement-${e}`}
                />
              ))}
            </Stack>
          ) : (
            '—'
          )}
        </Field>
      </Box>
    );
  }

  return (
    <>
      {body}

      <ConfirmDeleteTenantDialog
        tenant={confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={handleConfirmDelete}
      />

      <EditTenantDialog
        tenant={editTarget}
        onClose={() => setEditTarget(null)}
        onSuccess={onUpdated}
      />
    </>
  );
}
